import { Link } from 'react-router-dom';

/**
 * UI Harness Index Page
 * 
 * Entry point for UI testing without authentication
 */
export default function UIHarnessIndex() {
  const sections = [
    {
      title: 'AppLayout (Customer Dashboard)',
      description: 'Orange-themed customer-facing interface',
      routes: [
        { name: 'Dashboard Overview', path: '/__ui-harness/dashboard' },
        { name: 'Wallet', path: '/__ui-harness/wallet' },
        { name: 'Campaigns', path: '/__ui-harness/campaigns' },
        { name: 'Analytics', path: '/__ui-harness/analytics' },
        { name: 'Customers', path: '/__ui-harness/customers' },
        { name: 'Templates', path: '/__ui-harness/templates' },
      ],
    },
    {
      title: 'AdminLayout (Super Admin)',
      description: 'Purple-themed admin interface',
      routes: [
        { name: 'SMS Packs Management', path: '/__ui-harness/admin/sms-packs' },
        { name: 'Promo Codes Management', path: '/__ui-harness/admin/promo-codes' },
      ],
    },
    {
      title: 'CWLayout (CloudWaitress Style)',
      description: 'Red/dark-themed CloudWaitress-inspired interface',
      routes: [
        { name: 'Dashboard', path: '/__ui-harness/cw/dashboard' },
        { name: 'Wallet', path: '/__ui-harness/cw/wallet' },
        { name: 'Campaigns', path: '/__ui-harness/cw/campaigns' },
        { name: 'Analytics', path: '/__ui-harness/cw/analytics' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <div className="container mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-purple-600 text-white rounded-full mb-6">
            <div className="w-3 h-3 bg-yellow-300 rounded-full animate-pulse"></div>
            <span className="font-bold">UI HARNESS MODE</span>
          </div>
          
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            DAZZA UI Testing Harness
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Test all layouts and pages without authentication
          </p>
          <p className="text-sm text-gray-500">
            Mock data • No Clerk • No API calls • Safe to delete
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {sections.map((section, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {section.title}
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {section.description}
              </p>
              
              <div className="space-y-2">
                {section.routes.map((route, routeIdx) => (
                  <Link
                    key={routeIdx}
                    to={route.path}
                    className="block px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    {route.name} →
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-2xl mx-auto bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <h3 className="font-bold text-yellow-900 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Important Notes
          </h3>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• This harness does NOT bypass authentication in production routes</li>
            <li>• All data is mocked - no real API calls are made</li>
            <li>• Clerk is NOT loaded in harness routes</li>
            <li>• Safe to delete entire `/harness` folder and routes</li>
            <li>• Use for visual testing, layout alignment, and pre-launch QA</li>
          </ul>
        </div>

        <div className="mt-8 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium"
          >
            ← Back to Production App
          </Link>
        </div>
      </div>
    </div>
  );
}
